import React, { useEffect, useState } from 'react';
import { motion, useSpring, useTransform } from 'framer-motion';
import { CalculatorIcon, SlidersHorizontalIcon } from 'lucide-react';
import { InteriorSlide } from './SlideShell';
import type { SlideProps } from './SlideShell';
type LifecyclePhase = 'launch' | 'growth' | 'foundational';
interface PhaseConfig {
  label: string;
  portfolioLabel: string;
  sliderLabel: string;
  share: number;
  min: number;
  max: number;
  rationale: string;
}
const PHASES: Record<LifecyclePhase, PhaseConfig> = {
  launch: {
    label: 'Launch',
    portfolioLabel: 'Launch Brands',
    sliderLabel: '% of revenue from Launch Brands',
    share: 8,
    min: 1,
    max: 25,
    rationale: 'For Launch Brands, the model illustrates the revenue value of improving early adoption across the selected share of group revenue.'
  },
  growth: {
    label: 'Growth',
    portfolioLabel: 'Growth Brands',
    sliderLabel: '% of revenue from Growth Brands',
    share: 25,
    min: 5,
    max: 60,
    rationale: 'For Growth Brands, the model illustrates the revenue value of increasing adoption, frequency, or share across the selected portfolio.'
  },
  foundational: {
    label: 'Foundational',
    portfolioLabel: 'Foundational Brands',
    sliderLabel: '% of revenue from Foundational Brands',
    share: 13.5,
    min: 5,
    max: 40,
    rationale: 'For Foundational Brands, the model illustrates how a small improvement can unlock meaningful value from an established revenue base, even as resources shift toward newer products.'
  }
};
export function SlideValueCalculator({}: SlideProps) {
  const [phase, setPhase] = useState<LifecyclePhase>('foundational');
  const [revenueB, setRevenueB] = useState(32);
  const [phasePercent, setPhasePercent] = useState(0);
  const [improvementPercent, setImprovementPercent] = useState(0.5);
  const [animationDone, setAnimationDone] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const phaseConfig = PHASES[phase];
  useEffect(() => {
    const timers = [setTimeout(() => setPhasePercent(PHASES.foundational.share), 1000), setTimeout(() => setImprovementPercent(1), 1800), setTimeout(() => setAnimationDone(true), 2800)];
    return () => timers.forEach(clearTimeout);
  }, []);
  const upside = revenueB * 1000 * (phasePercent / 100) * (improvementPercent / 100);
  const upsideValue = useSpring(0, {
    bounce: 0,
    duration: 1600
  });
  const displayUpside = useTransform(upsideValue, latest => Math.round(latest).toLocaleString());
  useEffect(() => {
    if (revealed) upsideValue.set(upside);
  }, [revealed, upside, upsideValue]);
  const handleChange = (setter: (value: number) => void) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setter(Number(event.target.value));
  };
  const selectPhase = (nextPhase: LifecyclePhase) => {
    setPhase(nextPhase);
    setPhasePercent(PHASES[nextPhase].share);
  };
  const sliders = [{
    label: 'Group Revenue',
    value: `€${revenueB.toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: 5,
    max: 65,
    step: 0.5,
    current: revenueB,
    onChange: handleChange(setRevenueB),
    footer: ['€5bn', 'Illustrative: €32bn', '€65bn']
  }, {
    label: phaseConfig.sliderLabel,
    value: `€${(revenueB * phasePercent / 100).toFixed(1)}bn`,
    valueClass: 'text-[var(--fg)]',
    min: phaseConfig.min,
    max: phaseConfig.max,
    step: 0.5,
    current: phasePercent,
    onChange: handleChange(setPhasePercent),
    footer: [`${phaseConfig.min}%`, `${phasePercent.toFixed(1)}% — illustrative`, `${phaseConfig.max}%`]
  }, {
    label: 'Target Improvement',
    value: `${improvementPercent.toFixed(1)}%`,
    valueClass: 'text-[var(--brand)]',
    min: 0.5,
    max: 5,
    step: 0.5,
    current: improvementPercent,
    onChange: handleChange(setImprovementPercent),
    footer: ['0.5%', '', '5%']
  }];
  return <InteriorSlide data-id="element-447" data-mp="ggpg3ub3auh5">
      <motion.header initial={{
      opacity: 0,
      y: 20
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6
    }} className="mb-[clamp(1rem,1.8vw,1.6rem)] flex-shrink-0" data-id="element-448" data-mp="gjbnntxzd9zd">
        <div className="mb-3 flex items-center gap-3" data-id="element-449" data-mp="vehv8lcq5gut">
          <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-450" data-mp="vd6ukueksp7x">
            Illustrative model
          </span>
          <span className="h-px w-12 bg-black/10" data-id="element-451" data-mp="2pu62eouu7bz" />
          <span className="text-[11px] uppercase tracking-[0.16em] text-[var(--muted)]" data-id="element-452" data-mp="2qm2frqa093b">
            Across the full lifecycle
          </span>
        </div>

        <motion.h2 initial={{
        opacity: 0,
        y: 5
      }} animate={{
        opacity: 1,
        y: 0
      }} className="max-w-5xl text-[clamp(1.6rem,3vw,2.75rem)] font-bold leading-[1.1] tracking-tight text-[var(--fg)]" data-id="element-453" data-mp="cfdqhos7u98y">
          Even a{' '}
          <span className="text-[var(--brand)]" data-id="element-454" data-mp="3g2w6ayjzfuwe">
            {improvementPercent.toFixed(1)}%
          </span>{' '}
          improvement across the lifecycle equals
        </motion.h2>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 items-center gap-8 lg:grid-cols-12 lg:gap-14" data-id="element-455" data-mp="1t5wna07ajjqq">
        <div className="relative space-y-5 rounded-3xl border border-black/5 bg-[var(--surface)] p-[clamp(1.25rem,2vw,2rem)] lg:col-span-5" data-id="element-456" data-mp="1ob0v90220wdm">
          <div data-id="element-457" data-mp="1eba1j0lbilel">
            <div className="mb-2 flex items-center gap-2 text-[var(--brand)]" data-id="element-458" data-mp="1ebs2ubmu7ptd">
              <SlidersHorizontalIcon className="h-4 w-4" aria-hidden="true" data-id="element-459" data-mp="19ji549fhdlgj" />
              <p className="text-[10px] font-bold uppercase tracking-[0.15em]" data-id="element-460" data-mp="15ig69tph7bf3">
                Select lifecycle phase
              </p>
            </div>
            <div className="grid grid-cols-3 rounded-xl border border-[var(--brand)]/15 bg-white p-1 shadow-sm" role="group" aria-label="Select lifecycle phase" data-id="element-461" data-mp="10kn2t8fk4vii">
              {(Object.keys(PHASES) as LifecyclePhase[]).map(phaseKey => {
              const active = phase === phaseKey;
              return <button key={phaseKey} type="button" onClick={() => selectPhase(phaseKey)} aria-pressed={active} className={`rounded-lg px-2 py-2 text-xs font-bold transition ${active ? 'bg-[var(--brand)] text-white shadow-sm' : 'text-[var(--muted)] hover:bg-[var(--surface)] hover:text-[var(--fg)]'}`} data-id="element-462" data-mp="lwwp4t8ztfq6">
                    {PHASES[phaseKey].label}
                  </button>;
            })}
            </div>
          </div>

          {sliders.map(slider => <div key={slider.label} className="space-y-2.5" data-id="element-463" data-mp="1imdvzbjpp7tg">
              <div className="flex items-end justify-between gap-4" data-id="element-464" data-mp="1il2p32uyzjy4">
                <label htmlFor={slider.label} className="text-sm font-semibold text-[var(--fg)]" data-id="element-465" data-mp="1nf6ydsrpk42d">
                  {slider.label}
                </label>
                <span className={`text-2xl font-bold ${slider.valueClass}`} data-id="element-466" data-mp="334oaury6cbpc">
                  {slider.value}
                </span>
              </div>
              <input id={slider.label} type="range" min={slider.min} max={slider.max} step={slider.step} value={slider.current} onChange={slider.onChange} className="deck-slider w-full" data-id="element-467" data-mp="3cu7wg5d9ujnw" />
              <div className="flex justify-between text-xs text-[var(--muted)]" data-id="element-468" data-mp="2z44dpodlp837">
                <span data-id="element-469" data-mp="2u405smqrdlol">{slider.footer[0]}</span>
                <span className="text-[var(--brand)]" data-id="element-470" data-mp="2u6cox3896238">{slider.footer[1]}</span>
                <span data-id="element-471" data-mp="ebkhjsp6khlb">{slider.footer[2]}</span>
              </div>
            </div>)}
        </div>

        <div className="flex flex-col justify-center lg:col-span-7" data-id="element-472" data-mp="9dreh8j6c3p9">
          {revealed ? <motion.div key={`revealed-${phase}`} initial={{
          opacity: 0,
          scale: 0.96
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="space-y-4" data-id="element-473" data-mp="22ykw791f9k7e">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-474" data-mp="2hn8o95c27bsl">
                Unlocked Value
              </h3>
              <div className="flex items-baseline gap-2 text-[var(--brand)]" data-id="element-475" data-mp="2qm2hqmu3fm1x">
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-476" data-mp="2qo6vrxlpi1fx">€</span>
                <motion.span className="text-[clamp(3.5rem,8vw,7rem)] font-bold leading-none tracking-tight" data-id="element-477" data-mp="2vm8euij4u0re">
                  {displayUpside}
                </motion.span>
                <span className="text-[clamp(1.75rem,3vw,3rem)] font-bold" data-id="element-478" data-mp="118pi3awxem75">m</span>
              </div>
              <p className="max-w-xl border-l-2 border-[var(--brand)]/40 py-1 pl-4 text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-479" data-mp="167a9qly43xjz">
                {phaseConfig.rationale}
              </p>
            </motion.div> : <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.35,
          ease: [0.23, 1, 0.32, 1]
        }} className="max-w-xl space-y-6" data-id="element-480" data-mp="owwvwmxk3wec">
              <h3 className="text-base font-semibold uppercase tracking-[0.2em] text-[var(--muted)]" data-id="element-481" data-mp="1hlbxa6nrmg77">
                Unlocked Value
              </h3>
              <motion.p key={`rationale-${phase}`} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} className="text-[clamp(0.95rem,1.2vw,1.1rem)] leading-relaxed text-[var(--muted)]" data-id="element-482" data-mp="17ne9tldqcnfn">
                {phaseConfig.rationale}
              </motion.p>
              <motion.button type="button" onClick={() => setRevealed(true)} whileHover={{
            scale: 1.03
          }} whileTap={{
            scale: 0.98
          }} animate={{
            opacity: animationDone ? 1 : 0.4
          }} className="inline-flex items-center gap-2 rounded-full bg-[var(--brand)] px-8 py-4 text-lg font-bold text-white shadow-lg shadow-[var(--brand)]/25 transition-colors hover:bg-[var(--brand-light)]" data-id="element-483" data-mp="3ndr1tdnkb25r">
                <CalculatorIcon className="h-5 w-5" aria-hidden="true" data-id="element-484" data-mp="3ept7728ke2q5" />
                Calculate potential
              </motion.button>
            </motion.div>}
        </div>
      </div>
    </InteriorSlide>;
}